import { css } from 'styled-components';
import token from 'styled-token';
import { StyledIcon } from '../../components/Icon/Icon.styles';
import { StyledProgressStepperStepDot } from '../../components/ProgressStepperStepDot/ProgressStepperStepDot.styles';
import { StyledProgressStepperStepLabel } from '../../components/ProgressStepperStepLabel/ProgressStepperStepLabel.styles';
import { StyledProgressStepperStepLabelText } from '../../components/ProgressStepperStepLabelText/ProgressStepperStepLabelText.styles';
import { ORIENTATIONS, PROGRESS_STEPPER_APPEARANCES, PROGRESS_STEPPER_LABEL_ALIGNMENTS } from '../../constants';
import { hoverMediaQueryMixin } from '../hoverMediaQueryMixin';
import { inlineMediaQueryMixin } from '../inlineMediaQueryMixin';
import { spaceMixin } from '../spaceMixin';

// The "warning" icon is a triangle instead of a circle so it needs different styles.
var warningCSS = function warningCSS(props) {
  var active = props.active,
    appearance = props.appearance,
    interactive = props.interactive;
  if (appearance !== PROGRESS_STEPPER_APPEARANCES.WARNING || active) {
    return '';
  }
  return token({
    filler: 'ProgressStepper.status.warning.filler',
    iconSize: 'ProgressStepper.status.warning.iconSize',
    interactiveStepSize: 'ProgressStepper.interactiveStepSize',
    stepBackgroundColor: 'ProgressStepper.stepBackgroundColor'
  }, function (_ref) {
    var filler = _ref.filler,
      iconSize = _ref.iconSize,
      interactiveStepSize = _ref.interactiveStepSize,
      stepBackgroundColor = _ref.stepBackgroundColor;
    return css(["", "{background-color:transparent;box-shadow:none;position:relative;&:after{content:'';display:block;position:absolute;width:", "px;height:", "px;top:", "px;left:", "px;background-color:", ";z-index:-1;}", "{z-index:1;width:", "px;height:", "px;top:", "px;left:", "px;margin-bottom:", "px;position:relative;}}", ""], StyledProgressStepperStepDot, filler.width, filler.height, (interactiveStepSize - filler.height) / 2, (interactiveStepSize - filler.width) / 2, stepBackgroundColor, StyledIcon, iconSize, iconSize, (interactiveStepSize - iconSize) / 2 - 1, (interactiveStepSize - iconSize) / 2, interactiveStepSize - iconSize, interactive && hoverMediaQueryMixin("\n        " + StyledProgressStepperStepDot + StyledProgressStepperStepDot + " {\n          box-shadow: none;\n        }\n      "));
  })(props);
};
var appearanceCSS = function appearanceCSS(props) {
  var appearance = props.appearance;
  if (!appearance) {
    return '';
  }
  return token({
    boxShadow: "ProgressStepper.status." + appearance + ".boxShadow",
    color: "ProgressStepper.status." + appearance + ".foreground",
    currentStepSize: 'ProgressStepper.currentStepSize',
    interactiveStepSize: 'ProgressStepper.interactiveStepSize',
    ringSize: 'ProgressStepper.ringSize'
  }, function (_ref2) {
    var boxShadow = _ref2.boxShadow,
      color = _ref2.color,
      currentStepSize = _ref2.currentStepSize,
      interactiveStepSize = _ref2.interactiveStepSize,
      ringSize = _ref2.ringSize;
    return css(["padding-top:", "px;", "{box-shadow:", ";position:relative;width:", "px;height:", "px;}", ":before{width:", "px;height:", "px;}", "{color:", ";width:100%;height:100%;}", ""], (currentStepSize - interactiveStepSize) / 2, StyledProgressStepperStepDot, boxShadow, interactiveStepSize, interactiveStepSize, StyledProgressStepperStepDot, interactiveStepSize + ringSize, interactiveStepSize + ringSize, StyledIcon, color, warningCSS(props));
  })(props);
};
var activeCSS = function activeCSS(props) {
  var active = props.active;
  if (!active) {
    return '';
  }
  return token({
    activeStepBackgroundColor: 'ProgressStepper.activeStepBackgroundColor',
    activeStepBorderColor: 'ProgressStepper.activeStepBorderColor',
    activeStepLabelColor: 'ProgressStepper.activeStepLabelColor',
    stepBorderWidth: 'ProgressStepper.stepBorderWidth',
    boxShadow: "ProgressStepper.boxShadowRing",
    fontWeight: 'ProgressStepper.label.fontWeight.current',
    interactiveStepSize: 'ProgressStepper.interactiveStepSize',
    currentStepSize: 'ProgressStepper.currentStepSize'
  }, function (_ref3) {
    var activeStepBackgroundColor = _ref3.activeStepBackgroundColor,
      activeStepBorderColor = _ref3.activeStepBorderColor,
      activeStepLabelColor = _ref3.activeStepLabelColor,
      boxShadow = _ref3.boxShadow,
      fontWeight = _ref3.fontWeight,
      interactiveStepSize = _ref3.interactiveStepSize,
      currentStepSize = _ref3.currentStepSize,
      stepBorderWidth = _ref3.stepBorderWidth;
    return "\n            padding-top: " + (currentStepSize - interactiveStepSize) / 2 + "px;\n\n            " + StyledProgressStepperStepDot + " {\n                background-color: " + activeStepBackgroundColor + ";\n                border: " + stepBorderWidth + "px solid " + activeStepBorderColor + ";\n                box-shadow: " + boxShadow + ";\n                width: " + interactiveStepSize + "px;\n                height: " + interactiveStepSize + "px;\n            }\n\n            " + StyledProgressStepperStepLabelText + " {\n                color: " + activeStepLabelColor + ";\n                font-weight: " + fontWeight + ";\n            }\n        ";
  })(props);
};
var interactiveCSS = function interactiveCSS(props) {
  var active = props.active,
    _props$appearance = props.appearance,
    appearance = _props$appearance === void 0 ? PROGRESS_STEPPER_APPEARANCES.NONE : _props$appearance,
    interactive = props.interactive;
  if (!interactive) {
    return css(["&:not(:disabled){cursor:default;}"]);
  }
  return token({
    activeStepBoxShadow: "ProgressStepper.activeStepBoxShadow",
    boxShadow: "ProgressStepper.status." + appearance + ".boxShadow",
    fontWeight: 'ProgressStepper.label.fontWeight.interactive',
    interactiveLabelColor: 'ProgressStepper.interactiveLabelColor',
    dotTransitionProperty: 'ProgressStepper.dot.transition.property.interactive',
    dotTransitionDuration: 'ProgressStepper.dot.transition.duration.interactive',
    dotTransitionEasing: 'ProgressStepper.dot.transition.easing.interactive'
  }, function (_ref4) {
    var activeStepBoxShadow = _ref4.activeStepBoxShadow,
      boxShadow = _ref4.boxShadow,
      fontWeight = _ref4.fontWeight,
      interactiveLabelColor = _ref4.interactiveLabelColor,
      _ref4$dotTransitionPr = _ref4.dotTransitionProperty,
      dotTransitionProperty = _ref4$dotTransitionPr === void 0 ? 'transform' : _ref4$dotTransitionPr,
      _ref4$dotTransitionDu = _ref4.dotTransitionDuration,
      dotTransitionDuration = _ref4$dotTransitionDu === void 0 ? 0.2 : _ref4$dotTransitionDu,
      _ref4$dotTransitionEa = _ref4.dotTransitionEasing,
      dotTransitionEasing = _ref4$dotTransitionEa === void 0 ? 'ease' : _ref4$dotTransitionEa;
    // The ring of color used by some icons to fill in the gap between
    // the SVG and its Dot parent. (See appearanceCSS)
    var iconFiller = '';
    if (active) {
      iconFiller = activeStepBoxShadow + ", ";
    } else if (appearance !== PROGRESS_STEPPER_APPEARANCES.NONE) {
      iconFiller = "" + boxShadow;
    }
    return css(["", "{", ";font-weight:", ";}", ":before{transition-property:", ";transition-duration:", "s;transition-timing-function:", ";}", ""], StyledProgressStepperStepLabelText, active ? '' : "color: " + interactiveLabelColor, fontWeight, StyledProgressStepperStepDot, dotTransitionProperty, dotTransitionDuration, dotTransitionEasing, hoverMediaQueryMixin("\n            " + StyledProgressStepperStepDot + " {\n              box-shadow: " + iconFiller + ";\n            }\n\n            " + (!active ? "\n                    " + StyledProgressStepperStepDot + ":before {\n                      transform: translate(-50%, -50%) scale(1);\n                    }\n                  " : '') + "\n\n            " + StyledProgressStepperStepLabelText + " {\n                text-decoration: underline;\n            }\n        "));
  })(props);
};
var leftAlignCSS = function leftAlignCSS(props) {
  var active = props.active,
    alignLabel = props.alignLabel,
    appearance = props.appearance;
  if (alignLabel !== PROGRESS_STEPPER_LABEL_ALIGNMENTS.LEFT) {
    return '';
  }
  return token({
    nonInteractiveStepSize: 'ProgressStepper.nonInteractiveStepSize',
    progressBarHeight: 'ProgressMeter.sizes.sm'
  }, function (_ref5) {
    var nonInteractiveStepSize = _ref5.nonInteractiveStepSize,
      progressBarHeight = _ref5.progressBarHeight;
    return css(["align-items:flex-start;", "{margin-left:", ";}"], StyledProgressStepperStepDot, appearance || active ? '0' : (progressBarHeight - nonInteractiveStepSize) / 2 + "px");
  })(props);
};
var orientationCSS = function orientationCSS(props) {
  var active = props.active,
    appearance = props.appearance,
    orientation = props.orientation;
  if (orientation !== ORIENTATIONS.vertical) {
    return '';
  }
  return token({
    interactiveStepSize: 'ProgressStepper.interactiveStepSize',
    labelTextLineHeight: 'ProgressStepper.labelLineHeight',
    nonInteractiveStepSize: 'ProgressStepper.nonInteractiveStepSize'
  }, function (_ref6) {
    var interactiveStepSize = _ref6.interactiveStepSize,
      labelTextLineHeight = _ref6.labelTextLineHeight,
      nonInteractiveStepSize = _ref6.nonInteractiveStepSize;
    var dotSize = appearance || active ? interactiveStepSize : nonInteractiveStepSize;
    return css(["align-items:flex-start;flex-direction:row;padding-top:0;padding-bottom:", ";position:relative;&:nth-last-child(2){padding-bottom:0;}", "{flex-shrink:0;margin-top:", "px;margin-left:", "px;}", "{margin-top:0;margin-left:calc(", "px + ", ");text-align:left;}"], spaceMixin('md')(props), StyledProgressStepperStepDot, (labelTextLineHeight - dotSize) / 2, (interactiveStepSize - dotSize) / 2, StyledProgressStepperStepLabel, (interactiveStepSize - dotSize) / 2, spaceMixin(1.5)(props));
  });
};

/** @class StepButtonMixin */
/** @method */
export var ProgressStepperStepMixin = function ProgressStepperStepMixin(props) {
  return token({
    nonInteractiveLabelColor: 'ProgressStepper.nonInteractiveLabelColor',
    nonInteractiveStepSize: 'ProgressStepper.nonInteractiveStepSize',
    currentStepSize: 'ProgressStepper.currentStepSize',
    radius: 'ProgressStepper.dot.borderRadius',
    ringColor: 'ProgressStepper.ringColor',
    ringSize: 'ProgressStepper.ringSize',
    stepBackgroundColor: 'ProgressStepper.stepBackgroundColor',
    stepPaddingX: 'ProgressStepper.stepPaddingX'
  }, function (_ref7) {
    var nonInteractiveLabelColor = _ref7.nonInteractiveLabelColor,
      nonInteractiveStepSize = _ref7.nonInteractiveStepSize,
      currentStepSize = _ref7.currentStepSize,
      radius = _ref7.radius,
      ringColor = _ref7.ringColor,
      ringSize = _ref7.ringSize,
      stepBackgroundColor = _ref7.stepBackgroundColor,
      stepPaddingX = _ref7.stepPaddingX;
    return css(["align-items:center;box-sizing:border-box;cursor:default;display:flex;flex-direction:column;margin:0;padding-top:", "px;padding-right:", "px;padding-left:", "px;z-index:1;", "{position:relative;background-color:", ";border-radius:", ";box-sizing:border-box;width:", "px;height:", "px;}", ":before{display:block;content:'';position:absolute;top:50%;left:50%;z-index:-1;transform:translate(-50%,-50%) scale(0);transform-origin:center;background:", ";border-radius:", ";width:", "px;height:", "px;}", "{color:", ";}", " ", " ", " ", " ", ""], (currentStepSize - nonInteractiveStepSize) / 2, stepPaddingX, stepPaddingX, StyledProgressStepperStepDot, stepBackgroundColor, radius, nonInteractiveStepSize, nonInteractiveStepSize, StyledProgressStepperStepDot, ringColor, radius, nonInteractiveStepSize + ringSize, nonInteractiveStepSize + ringSize, StyledProgressStepperStepLabelText, nonInteractiveLabelColor, appearanceCSS(props), activeCSS(props), interactiveCSS(props), leftAlignCSS(props), inlineMediaQueryMixin('orientation', orientationCSS)(props));
  })(props);
};